<template>
  <div>
    <div class="space-y-4">
      <!-- Page header -->
      <div class="flex items-center gap-3">
        <span class="flex items-center justify-center w-10 h-10 rounded-xl bg-taxiye-100 text-taxiye-500">
          <span class="material-icons-round">calculate</span>
        </span>
        <div>
          <h2 class="text-lg font-semibold text-gray-800">Finance</h2>
          <p class="text-sm text-gray-500">Review trip finances and settlements.</p>
        </div>
      </div>

      <!-- Filters -->
      <div class="elevated-card p-4 bg-white border border-gray-200 rounded-2xl">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center space-x-1 text-gray-500">
            <span class="text-base material-icons-round">filter_list</span>
            <span class="text-xs font-semibold tracking-widest uppercase">Filters</span>
          </div>
          <export-button
            :downloadUrl="reportDownloadUrl"
            :working="loading"
          ></export-button>
        </div>

        <div class="flex flex-wrap items-end gap-4">
          <!-- Driver search -->
          <div class="relative w-full sm:w-64">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Driver</label>
            <div class="relative mt-1.5">
              <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <span class="text-base text-gray-500 material-icons-round">search</span>
              </span>
              <input
                v-model="searchText"
                @keyup="search"
                class="w-full h-10 pl-9 pr-3 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="Search drivers..."
              />
            </div>
            <transition
              enter-active-class="transition duration-100 ease-out"
              enter-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-75 ease-in"
              leave-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0"
            >
              <div v-if="drivers.length" class="absolute inset-x-0 z-50 mt-2">
                <div
                  class="py-1 overflow-y-auto bg-white border border-gray-100 shadow-lg rounded-xl max-h-64"
                >
                  <ul>
                    <li
                      v-for="(result, index) in drivers"
                      :key="`driver-${index}`"
                      @click="filterFinance('driver', result)"
                      class="px-3 py-2 cursor-pointer transition-colors duration-150 hover:bg-taxiye-50"
                    >
                      <p class="text-sm font-semibold text-gray-800">
                        {{ `${result.firstName} ${result.lastName}` }}
                      </p>
                      <p class="text-xs text-gray-500">
                        {{ result.phoneNumber }}
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            </transition>
          </div>

          <!-- Date range -->
          <div class="w-full sm:w-auto">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Date Range</label>
            <div class="flex items-center h-10 mt-1.5 bg-white border border-gray-300 shadow-sm rounded-xl">
              <span class="pl-3 pr-1 text-base text-gray-500 material-icons-round">event</span>
              <v-date-picker v-model="range" is-range>
                <template v-slot="{ inputValue, inputEvents }">
                  <div class="flex items-center">
                    <input
                      :value="inputValue.start"
                      v-on="inputEvents.start"
                      placeholder="From"
                      class="w-24 h-8 px-2 text-sm text-gray-800 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                    />
                    <span class="mx-1 text-base text-gray-300 material-icons-round">arrow_forward</span>
                    <input
                      :value="inputValue.end"
                      v-on="inputEvents.end"
                      placeholder="To"
                      class="w-24 h-8 px-2 text-sm text-gray-800 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                    />
                  </div>
                </template>
              </v-date-picker>
            </div>
          </div>

          <!-- Apply -->
          <button
            @click="filter"
            class="inline-flex items-center h-10 px-5 space-x-2 text-sm font-semibold text-white rounded-xl shadow-sm bg-taxiye-500 transition-colors duration-150 hover:bg-taxiye-400 focus:outline-none"
          >
            <span class="text-base material-icons-round">tune</span>
            <span>Apply Filters</span>
          </button>

          <!-- Cancel / clear filters -->
          <button
            @click="clearFilters"
            class="inline-flex items-center h-10 px-5 space-x-2 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-xl shadow-sm transition-colors duration-150 hover:bg-gray-50 hover:text-gray-800 focus:outline-none"
          >
            <span class="text-base material-icons-round">close</span>
            <span>Cancel Filter</span>
          </button>
        </div>
      </div>

      <!-- Trip / Rent breakdown -->
      <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <!-- Trip -->
        <section class="elevated-card p-5 bg-white border border-gray-200 rounded-2xl">
          <div class="flex items-center mb-4 space-x-2 text-gray-500">
            <span class="text-base material-icons-round">local_taxi</span>
            <h2 class="text-xs font-semibold tracking-widest uppercase">Trip</h2>
          </div>
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div class="stat-tile p-4 bg-white border border-gray-100 rounded-xl">
              <div class="flex items-center space-x-3">
                <span class="flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-xl bg-indigo-50">
                  <i class="text-xl text-indigo-600 material-icons-round">near_me</i>
                </span>
                <p class="text-xs font-extrabold tracking-widest text-gray-600 uppercase">Normal Trips</p>
              </div>
              <dl class="mt-3 space-y-1">
                <div class="flex items-baseline justify-between">
                  <dt class="text-xs font-medium text-gray-400">Fare</dt>
                  <dd class="text-lg font-bold text-gray-900">
                    {{ formatMoney(finance.normalTripsFare) }}
                    <span class="text-xs font-semibold text-gray-400">ETB</span>
                  </dd>
                </div>
                <div class="flex items-baseline justify-between">
                  <dt class="text-xs font-medium text-gray-400">Net</dt>
                  <dd class="text-lg font-bold text-gray-900">
                    {{ formatMoney(finance.normalTripsNet) }}
                    <span class="text-xs font-semibold text-gray-400">ETB</span>
                  </dd>
                </div>
              </dl>
            </div>

            <div class="stat-tile p-4 bg-white border border-gray-100 rounded-xl">
              <div class="flex items-center space-x-3">
                <span class="flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-xl bg-blue-50">
                  <i class="text-xl text-blue-600 material-icons-round">business_center</i>
                </span>
                <p class="text-xs font-extrabold tracking-widest text-gray-600 uppercase">Corporate Trips</p>
              </div>
              <dl class="mt-3 space-y-1">
                <div class="flex items-baseline justify-between">
                  <dt class="text-xs font-medium text-gray-400">Fare</dt>
                  <dd class="text-lg font-bold text-gray-900">
                    {{ formatMoney(finance.corporateTripsFare) }}
                    <span class="text-xs font-semibold text-gray-400">ETB</span>
                  </dd>
                </div>
                <div class="flex items-baseline justify-between">
                  <dt class="text-xs font-medium text-gray-400">Net</dt>
                  <dd class="text-lg font-bold text-gray-900">
                    {{ formatMoney(finance.corporateTripsNet) }}
                    <span class="text-xs font-semibold text-gray-400">ETB</span>
                  </dd>
                </div>
              </dl>
            </div>

            <div class="stat-tile p-4 bg-white border border-gray-100 rounded-xl">
              <div class="flex items-center space-x-3">
                <span class="flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-xl bg-green-50">
                  <i class="text-xl text-green-600 material-icons-round">receipt_long</i>
                </span>
                <p class="text-xs font-extrabold tracking-widest text-gray-600 uppercase">Tax</p>
              </div>
              <p class="mt-3 text-2xl font-black leading-none text-gray-900">
                {{ formatMoney(finance.normalTripsTax + finance.corporateTripsTax) }}
                <span class="text-xs font-semibold text-gray-400">ETB</span>
              </p>
            </div>

            <div class="stat-tile p-4 bg-white border border-gray-100 rounded-xl">
              <div class="flex items-center space-x-3">
                <span class="flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-xl bg-teal-50">
                  <i class="text-xl text-teal-600 material-icons-round">functions</i>
                </span>
                <p class="text-xs font-extrabold tracking-widest text-gray-600 uppercase">Total</p>
              </div>
              <dl class="mt-3 space-y-1">
                <div class="flex items-baseline justify-between">
                  <dt class="text-xs font-medium text-gray-400">Fare</dt>
                  <dd class="text-lg font-bold text-gray-900">
                    {{ formatMoney(finance.normalTripsFare + finance.corporateTripsFare) }}
                    <span class="text-xs font-semibold text-gray-400">ETB</span>
                  </dd>
                </div>
                <div class="flex items-baseline justify-between">
                  <dt class="text-xs font-medium text-gray-400">Net</dt>
                  <dd class="text-lg font-bold text-gray-900">
                    {{ formatMoney(finance.normalTripsNet + finance.corporateTripsNet) }}
                    <span class="text-xs font-semibold text-gray-400">ETB</span>
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </section>

        <!-- Rent -->
        <section class="elevated-card p-5 bg-white border border-gray-200 rounded-2xl">
          <div class="flex items-center mb-4 space-x-2 text-gray-500">
            <span class="text-base material-icons-round">car_rental</span>
            <h2 class="text-xs font-semibold tracking-widest uppercase">Rent</h2>
          </div>
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div class="stat-tile p-4 bg-white border border-gray-100 rounded-xl">
              <div class="flex items-center space-x-3">
                <span class="flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-xl bg-indigo-50">
                  <i class="text-xl text-indigo-600 material-icons-round">payments</i>
                </span>
                <p class="text-xs font-extrabold tracking-widest text-gray-600 uppercase">Rent Fare</p>
              </div>
              <p class="mt-3 text-2xl font-black leading-none text-gray-900">
                {{ formatMoney(finance.rentFare) }}
                <span class="text-xs font-semibold text-gray-400">ETB</span>
              </p>
            </div>

            <div class="stat-tile p-4 bg-white border border-gray-100 rounded-xl">
              <div class="flex items-center space-x-3">
                <span class="flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-xl bg-blue-50">
                  <i class="text-xl text-blue-600 material-icons-round">account_balance_wallet</i>
                </span>
                <p class="text-xs font-extrabold tracking-widest text-gray-600 uppercase">Rent Net</p>
              </div>
              <p class="mt-3 text-2xl font-black leading-none text-gray-900">
                {{ formatMoney(finance.rentNet) }}
                <span class="text-xs font-semibold text-gray-400">ETB</span>
              </p>
            </div>

            <div class="stat-tile p-4 bg-white border border-gray-100 rounded-xl">
              <div class="flex items-center space-x-3">
                <span class="flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-xl bg-green-50">
                  <i class="text-xl text-green-600 material-icons-round">receipt_long</i>
                </span>
                <p class="text-xs font-extrabold tracking-widest text-gray-600 uppercase">Rent Tax</p>
              </div>
              <p class="mt-3 text-2xl font-black leading-none text-gray-900">
                {{ formatMoney(finance.rentTax) }}
                <span class="text-xs font-semibold text-gray-400">ETB</span>
              </p>
            </div>
          </div>
        </section>
      </div>

      <!-- Payment summary -->
      <div>
        <div class="flex items-center mb-3 space-x-2 text-gray-500">
          <span class="text-base material-icons-round">summarize</span>
          <h2 class="text-xs font-semibold tracking-widest uppercase">Payment Summary</h2>
        </div>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div class="summary-card elevated-card flex items-center p-5 space-x-4 bg-white border border-gray-200 rounded-2xl">
            <span class="flex items-center justify-center flex-shrink-0 w-12 h-12 rounded-xl shadow-sm bg-green-50">
              <i class="text-2xl text-green-600 material-icons-round">payments</i>
            </span>
            <div>
              <p class="text-xs font-extrabold tracking-widest text-gray-600 uppercase">Fare</p>
              <p class="mt-1 text-3xl font-black leading-none text-gray-900">
                {{
                  formatMoney(
                    finance.normalTripsFare +
                      finance.corporateTripsFare +
                      finance.rentFare
                  )
                }}
                <span class="text-xs font-semibold text-gray-400">ETB</span>
              </p>
            </div>
          </div>

          <div class="summary-card elevated-card flex items-center p-5 space-x-4 bg-white border border-gray-200 rounded-2xl">
            <span class="flex items-center justify-center flex-shrink-0 w-12 h-12 rounded-xl shadow-sm bg-indigo-50">
              <i class="text-2xl text-indigo-600 material-icons-round">receipt_long</i>
            </span>
            <div>
              <p class="text-xs font-extrabold tracking-widest text-gray-600 uppercase">Tax</p>
              <p class="mt-1 text-3xl font-black leading-none text-gray-900">
                {{
                  formatMoney(
                    finance.corporateTripsTax +
                      finance.normalTripsTax +
                      finance.rentTax
                  )
                }}
                <span class="text-xs font-semibold text-gray-400">ETB</span>
              </p>
            </div>
          </div>

          <div class="summary-card elevated-card flex items-center p-5 space-x-4 bg-white border border-gray-200 rounded-2xl">
            <span class="flex items-center justify-center flex-shrink-0 w-12 h-12 rounded-xl shadow-sm bg-teal-50">
              <i class="text-2xl text-teal-600 material-icons-round">account_balance_wallet</i>
            </span>
            <div>
              <p class="text-xs font-extrabold tracking-widest text-gray-600 uppercase">Net</p>
              <p class="mt-1 text-3xl font-black leading-none text-gray-900">
                {{
                  formatMoney(
                    finance.normalTripsNet +
                      finance.corporateTripsNet +
                      finance.rentNet
                  )
                }}
                <span class="text-xs font-semibold text-gray-400">ETB</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Payment records -->
      <div class="flex flex-wrap items-end justify-between gap-4">
        <div class="flex items-center space-x-2 text-gray-500">
          <span class="text-base material-icons-round">list_alt</span>
          <h2 class="text-xs font-semibold tracking-widest uppercase">Payment Records</h2>
        </div>
        <div class="flex items-end space-x-4">
          <div class="w-full sm:w-36">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Status</label>
            <select
              class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
              v-model="selectedFilter"
              @change="getRides"
            >
              <option :selected="selectedFilter === 'all'" value="all">
                All
              </option>
              <option
                :selected="selectedFilter === 'Completed'"
                value="Completed"
              >
                Completed
              </option>
              <option :selected="selectedFilter === 'Canceled'" value="Canceled">
                Canceled
              </option>
              <option :selected="selectedFilter === 'Started'" value="Started">
                Started
              </option>
            </select>
          </div>
          <export-button
            :downloadUrl="tripsReportDownloadUrl"
            :working="loading"
          ></export-button>
        </div>
      </div>

      <div class="elevated-card overflow-hidden bg-white border border-gray-200 rounded-xl">
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead>
              <tr class="bg-gray-100 border-b border-gray-200">
                <th
                  v-for="col in [
                    'Passenger Name',
                    'Driver Name',
                    'Pick / Drop Address',
                    'Date',
                    'Type',
                    'Vehicle Type',
                    'Fare',
                    'Status',
                  ]"
                  :key="col"
                  class="px-5 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase whitespace-no-wrap"
                >
                  {{ col }}
                </th>
                <th class="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody class="relative" v-if="rides.length">
              <tr
                v-for="(trip, index) in rides"
                :key="trip._id"
                class="transition-colors duration-150 hover:bg-taxiye-50"
                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-100'"
              >
                <!-- Passenger -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <div class="flex items-center space-x-3">
                    <span
                      class="flex items-center justify-center flex-shrink-0 w-10 h-10 text-xs font-bold rounded-full"
                      :class="avatarClass(personLabel(trip.passenger))"
                    >{{ initials(personLabel(trip.passenger)) }}</span>
                    <span class="font-medium text-gray-900">{{ personLabel(trip.passenger, "Unknown") }}</span>
                  </div>
                </td>
                <!-- Driver -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <div class="flex items-center space-x-3">
                    <span
                      class="flex items-center justify-center flex-shrink-0 w-10 h-10 text-xs font-bold rounded-full"
                      :class="avatarClass(personLabel(trip.driver))"
                    >{{ initials(personLabel(trip.driver)) }}</span>
                    <span class="text-gray-700">{{ personLabel(trip.driver, "Unknown") }}</span>
                  </div>
                </td>
                <!-- Route -->
                <td class="px-5 py-4" style="min-width: 200px">
                  <div class="flex">
                    <div class="flex flex-col items-center mr-3">
                      <i class="text-base leading-none text-green-500 material-icons-round">my_location</i>
                      <span class="flex-1 w-px my-1 border-l border-gray-300 border-dashed"></span>
                      <i class="text-base leading-none text-red-500 material-icons-round">flag</i>
                    </div>
                    <div class="flex flex-col justify-between min-w-0">
                      <p class="text-gray-700 truncate max-w-xs">
                        {{ trip.pickUpAddress ? trip.pickUpAddress.name : "—" }}
                      </p>
                      <p class="text-gray-700 truncate max-w-xs">
                        {{ trip.dropOffAddress ? trip.dropOffAddress.name : "—" }}
                      </p>
                    </div>
                  </div>
                </td>
                <!-- Date -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  <div class="flex items-center space-x-2">
                    <i class="text-sm text-gray-400 material-icons-round">schedule</i>
                    <span>{{ formatDate(trip.createdAt) }}</span>
                  </div>
                </td>
                <!-- Type -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <span class="inline-flex px-2 py-1 text-xs font-medium text-gray-600 capitalize rounded-md bg-gray-100">
                    {{ trip.type }}
                  </span>
                </td>
                <!-- Vehicle type -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  {{ trip.vehicleType ? trip.vehicleType.name : "—" }}
                </td>
                <!-- Fare -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <span class="font-semibold text-gray-900">{{ formatMoney(trip.fare) }}</span>
                  <span class="text-xs text-gray-400">ETB</span>
                </td>
                <!-- Status -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <span
                    class="inline-flex items-center px-2 py-1 text-xs font-semibold leading-none capitalize rounded-full"
                    :class="statusClass(trip.status)"
                  >
                    <span class="w-2 h-2 mr-2 rounded-full" :class="statusDotClass(trip.status)"></span>
                    {{ prettyStatus(trip.status) }}
                  </span>
                </td>
                <!-- Action -->
                <td class="px-5 py-4 text-right whitespace-no-wrap">
                  <router-link
                    :to="'/finance/' + trip._id + '/show'"
                    title="View payment"
                    class="inline-flex items-center justify-center w-10 h-10 text-gray-400 rounded-lg transition-colors duration-150 hover:bg-taxiye-100 hover:text-taxiye-600"
                  >
                    <i class="material-icons-round">visibility</i>
                  </router-link>
                </td>
              </tr>
              <div
                v-if="loading"
                class="absolute inset-0 flex items-center justify-center w-full h-full bg-white bg-opacity-50"
              >
                <i class="text-5xl text-taxiye-500 material-icons-round spin">sync</i>
                <span class="invisible block">Loading...</span>
              </div>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="9" class="py-16 text-center bg-white">
                  <div v-if="loading" class="inline-block">
                    <i class="text-5xl text-taxiye-500 material-icons-round spin">sync</i>
                    <span class="invisible block">Loading...</span>
                  </div>
                  <div v-else class="flex flex-col items-center text-gray-400">
                    <span class="flex items-center justify-center w-16 h-16 mb-3 rounded-full bg-gray-100">
                      <i class="text-3xl material-icons-round">inbox</i>
                    </span>
                    <span class="text-sm font-medium text-gray-500">No payment records found</span>
                    <span class="mt-1 text-xs text-gray-400">Try adjusting your filters</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="flex items-end justify-between">
        <pagination
          class="flex space-x-2"
          :currentPage="currentPage"
          :total="total"
          @goto="goToPage"
          @setPerPage="setPerPage"
          :perPage="perPage"
          :start="start"
          :end="end"
          :count="paginator.count"
        ></pagination>
      </div>
    </div>
  </div>
</template>

<script>
// import moment from "moment";
import _ from "lodash";
import PaginationMixin from "@/mixins/Pagination";
import ExportButton from "@/components/ExportButton";
import Pagination from "@/components/Pagination";

export default {
  mixins: [PaginationMixin],
  data() {
    return {
      rides: [],
      openDropdown: false,
      loading: true,
      selectedFilter: "all",
      startDate: null,
      searchText: "",
      range: {
        start: null,
        end: null,
      },
      drivers: [],
      passengers: [],
      passengerId: "",
      driverId: "",
      finance: {
        normalTripsFare: 0,
        normalTripsNet: 0,
        normalTripsTax: 0,
        corporateTripsFare: 0,
        corporateTripsNet: 0,
        corporateTripsTax: 0,
        rentFare: 0,
        rentNet: 0,
        rentTax: 0,
      },
    };
  },
  components: {
    ExportButton,
    Pagination,
  },
  watch: {
    currentPage() {
      this.getRides();
    },
    perPage() {
      this.currentPage === 1 ? this.getRides() : (this.currentPage = 1);
    },
  },
  created() {
    var range = this.defaultRange();
    this.range.start = range.start;
    this.range.end = range.end;
    this.getFinance();
    this.getRides();
  },
  computed: {
    driverFilter() {
      return this.$route.query.driver || null;
    },
    riderFilter() {
      return this.$route.query.passenger || null;
    },
    tripsReportDownloadUrl() {
      var path = `trips/export?limit=${this.perPage}&page=${this.currentPage}&populate=["driver","passenger","vehicleType"]&from=${this.range.start}&to=${this.range.end}`;

      if (this.driverId) {
        path += "&driver=" + this.driverId;
      }
      if (this.selectedFilter && this.selectedFilter != "all") {
        path += "&status=" + this.selectedFilter;
      }

      return path;
    },
    reportDownloadUrl() {
      var path = `finance/export?from=${this.range.start}&to=${this.range.end}`;
      if (this.driverId) {
        path += "&driver=" + this.driverId;
      }
      return path;
    },
  },
  methods: {
    defaultRange() {
      var sd = new Date();
      sd.setDate(0);
      sd.setDate(1);
      var ed = new Date();
      ed.setMonth(ed.getMonth() + 1);
      ed.setDate(0);
      return { start: sd, end: ed };
    },
    filter() {
      this.getFinance();
      this.getRides();
    },
    clearFilters() {
      this.searchText = "";
      this.driverId = "";
      this.passengerId = "";
      this.drivers = [];
      this.passengers = [];
      this.selectedFilter = "all";
      var range = this.defaultRange();
      this.range.start = range.start;
      this.range.end = range.end;
      this.getFinance();
      // Reset to first page; the currentPage watcher reloads rides when it changes.
      if (this.currentPage !== 1) {
        this.currentPage = 1;
      } else {
        this.getRides();
      }
    },
    filterFinance(type, data) {
      this.passengerId = "";
      this.driverId = "";
      this.drivers = [];
      this.passengers = [];

      if (type == "passenger") {
        this.passengerId = data._id;
        this.searchText =
          data.firstName +
          " " +
          data.lastName +
          ", " +
          data.phoneNumber +
          ", " +
          type;
      } else if (type == "driver") {
        this.driverId = data._id;
        this.searchText =
          data.firstName + " " + data.lastName + ", " + data.phoneNumber;
      }
    },
    getFinance() {
      var path = `finance?from=${this.range.start}&to=${this.range.end}`;
      if (this.driverId) {
        path += "&driver=" + this.driverId;
      }
      this.$misc
        .get(path)
        .then((response) => {
          this.finance = response.data;
        })
        .catch((err) => console.log(err))
        .finally(() => (this.loading = false));
    },
    formatDate(date) {
      return date ? date.replace("T", " ").slice(0, 16) : "-";

      // return moment(date).format("MMM D, YYYY HH:mm");
    },
    formatMoney(amount) {
      return (amount || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    personLabel(person, fallback = "") {
      if (!person) return fallback;
      const name = `${person.firstName || ""} ${person.lastName || ""}`.trim();
      if (!name) return fallback;
      return name
        .split(" ")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    },
    initials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .filter(Boolean)
        .map((part) => part[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },
    avatarClass(name) {
      const palette = [
        "bg-taxiye-100 text-taxiye-600",
        "bg-indigo-50 text-indigo-600",
        "bg-blue-50 text-blue-600",
        "bg-green-50 text-green-600",
        "bg-purple-50 text-purple-600",
        "bg-pink-50 text-pink-600",
      ];
      let hash = 0;
      for (let i = 0; i < (name || "").length; i++) {
        hash = (hash + name.charCodeAt(i)) % palette.length;
      }
      return palette[hash];
    },
    statusClass(status) {
      const s = (status || "").toLowerCase();
      if (s.includes("cancel") || s.includes("no_drivers")) return "text-red-700 bg-red-100";
      if (s.includes("complet")) return "text-green-700 bg-green-100";
      if (s.includes("start") || s.includes("running") || s.includes("progress")) return "text-orange-700 bg-orange-100";
      return "text-gray-700 bg-gray-100";
    },
    statusDotClass(status) {
      const s = (status || "").toLowerCase();
      if (s.includes("cancel") || s.includes("no_drivers")) return "bg-red-500";
      if (s.includes("complet")) return "bg-green-500";
      if (s.includes("start") || s.includes("running") || s.includes("progress")) return "bg-orange-500";
      return "bg-gray-400";
    },
    prettyStatus(status) {
      return (status || "—").replace(/_/g, " ").toLowerCase();
    },
    toggleDropdown(index) {
      if (this.openDropdown === index) {
        this.openDropdown = false;
      } else {
        this.openDropdown = index;
      }
    },
    search: _.debounce(function (e) {
      if (!e.target.value.trim()) {
        this.results = [];
        this.drivers = [];
        this.passengers = [];
        this.driverId = "";
        this.passengerId = "";
        this.getRides();
        return;
      }

      this.$misc
        .get(`/drivers/search?limit=5&q=${e.target.value}`)
        .then((response) => {
          this.drivers = response.data;
        })
        .catch((err) => console.log(err));
    }, 500),
    getRides() {
      this.loading = true;
      var path = `trips?limit=${this.perPage}&page=${this.currentPage}&populate=["driver","passenger","vehicleType"]&from=${this.range.start}&to=${this.range.end}`;

      if (this.driverId) {
        path += "&driver=" + this.driverId;
      }
      if (this.selectedFilter && this.selectedFilter != "all") {
        path += "&status=" + this.selectedFilter;
      }

      this.$misc
        .get(path)
        .then((response) => {
          this.rides = response.data.data;
          this.paginator = {
            nextPage: response.data.nextPage,
            prevPage: response.data.prevPage,
            count: response.data.count,
          };
        })
        .catch((err) => console.log(err))
        .finally(() => (this.loading = false));
    },
  },
};
</script>

<style scoped>
/* Cards — elevated at rest, matching the dashboard's warm layered shadow */
.elevated-card {
  box-shadow: 0 2px 4px rgba(77, 50, 0, 0.04), 0 12px 24px -8px rgba(77, 50, 0, 0.12);
}

/* Small stat tiles inside the Trip / Rent sections — subtle elevation, gentle lift on hover */
.stat-tile {
  box-shadow: 0 1px 2px rgba(77, 50, 0, 0.04), 0 6px 12px -4px rgba(77, 50, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.stat-tile:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 4px rgba(77, 50, 0, 0.05), 0 10px 20px -6px rgba(77, 50, 0, 0.16);
}

/* Summary cards lift higher on hover */
.summary-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}
.summary-card:hover {
  transform: translateY(-3px);
  border-color: #e2e8f0;
  box-shadow: 0 4px 8px rgba(77, 50, 0, 0.05), 0 20px 36px -10px rgba(77, 50, 0, 0.18);
}
</style>
